import { AxiosResponse } from 'axios';
import FormData from 'form-data';
import Archivable, { ArchivedUpdatedEvent } from './shared/archivable';
import Entity from './shared/entity';
import { Event } from './shared/eventstore-client/EventstoreClient';
import { ServiceClientWithEventstore } from './shared/service-client/ServiceClientWithEventstore';

export interface FileInfo extends Entity, Archivable {
  uri: string;
  checksum: string;
  contentType: string;
  title?: string;
  altText?: string;
  folder?: string | Folder;
  tags?: string[];
}

export enum Folder {
  OWNERSHIP_REQUESTS = 'OWNERSHIP_REQUESTS',
}

export type UploadFileRequest = Pick<FileInfo, 'title' | 'altText'> & {
  file: any;
  folder?: Folder;
};

export type UpdateFileRequest = Pick<FileInfo, 'title' | 'altText' | 'tags'>;

export interface GetAllFilesRequest {
  continuationToken?: string;
  limit?: number;
  allowArchived?: boolean;
  excludeFolders?: string[];
}

export interface GetAllFilesResponse {
  files: FileInfo[];
  continuationToken?: string;
}

export type PERMISSIONS =
  | typeof CREATE
  | typeof UPDATE
  | typeof GET_ALL
  | typeof SET_ARCHIVED
  | typeof GET_EVENTS_ANY
  | typeof GET_EVENTS_CREATED
  | typeof GET_EVENTS_UPDATED;

export const CREATE = 'FILE:CREATE';
export const UPDATE = 'FILE:UPDATE';
export const GET_ALL = 'FILE:GET_ALL';
export const SET_ARCHIVED = 'FILE:SET_ARCHIVED';

export const GET_EVENTS_ANY = 'FILE:GET_EVENTS:ANY';
export const GET_EVENTS_CREATED = 'FILE:GET_EVENTS:CREATED';
export const GET_EVENTS_UPDATED = 'FILE:GET_EVENTS:UPDATED';
export const GET_EVENTS_ARCHIVED_UPDATED = 'FILE:GET_EVENTS:ARCHIVED_UPDATED';

export const PERMISSION_NAMES = {
  CREATE,
  UPDATE,
  GET_ALL,
  SET_ARCHIVED,
  GET_EVENTS_ANY,
  GET_EVENTS_CREATED,
  GET_EVENTS_UPDATED,
  GET_EVENTS_ARCHIVED_UPDATED,
};

export interface CreatedEvent extends Event {
  type: 'CREATED';
  data: FileInfo;
}

export interface UpdatedEvent extends Event {
  type: 'CREATED';
  data: FileInfo;
}

export type FileEvents = CreatedEvent | ArchivedUpdatedEvent | UpdatedEvent;

export class FileClient extends ServiceClientWithEventstore<FileEvents> {
  public async upload({ file, title, altText, folder }: UploadFileRequest): Promise<AxiosResponse<FileInfo>> {
    const fd = new FormData();
    fd.append('uploadfile', file);
    if (title) {
      fd.append('title', title);
    }
    if (altText) {
      fd.append('altText', altText);
    }

    if (folder) {
      fd.append('folder', folder);
    }

    const headers = typeof fd.getHeaders === 'function' ? fd.getHeaders() : {};
    return await this.httpClient.post('/', fd, { headers, ...this.requestConfig });
  }

  public async update(id: string, fileUpdates: UpdateFileRequest): Promise<AxiosResponse<FileInfo>> {
    return await this.httpClient.patch(`/${id}`, fileUpdates, this.requestConfig);
  }

  public async getAll(input: GetAllFilesRequest = {}): Promise<AxiosResponse<GetAllFilesResponse>> {
    if (!input.excludeFolders) {
      input.excludeFolders = [];
    }

    return await this.httpClient.get('/', {
      params: {
        ...input,
        excludeFolders: input.excludeFolders.join(','),
      },
      ...this.requestConfig,
    });
  }

  public async setArchived(id: string, archived: boolean): Promise<AxiosResponse<FileInfo>> {
    return await this.httpClient.put(`/${id}/archived`, { archived }, this.requestConfig);
  }
}
